import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { supabase } from "../lib/supabase";
import { C, Card, Btn, Stars, Chip, Badge, MetricCard, Avatar } from "../components/UI";

const CRITERIOS = [
  { campo: "nota_maternidade", label: "Política de maternidade" },
  { campo: "nota_flexibilidade", label: "Flexibilidade" },
  { campo: "nota_lideranca_feminina", label: "Liderança feminina" },
  { campo: "nota_ambiente", label: "Ambiente de trabalho" },
  { campo: "nota_salario", label: "Igualdade salarial" },
  { campo: "nota_crescimento", label: "Crescimento de carreira" },
  { campo: "nota_equilibrio", label: "Equilíbrio vida-trabalho" },
];

function media(lista, campo) {
  const valores = lista.map(r => r[campo]).filter(v => v !== null && v !== undefined);
  if (valores.length === 0) return null;
  return valores.reduce((a, b) => a + b, 0) / valores.length;
}

function Vazio({ titulo, texto, acao }) {
  return (
    <Card style={{ padding: "40px 32px", textAlign: "center" }}>
      <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 8 }}>{titulo}</div>
      <p style={{ fontSize: 13, color: C.textSec, lineHeight: 1.7, maxWidth: 420, margin: "0 auto" }}>{texto}</p>
      {acao}
    </Card>
  );
}

// ── Sidebar ────────────────────────────────────────────────────────────────────
function Sidebar({ active, setActive, empresa }) {
  const navigate = useNavigate();
  const { logout } = useApp();
  const items = [
    { id: "overview", icon: "📊", label: "Visão geral" },
    { id: "reviews", icon: "⭐", label: "Avaliações" },
    { id: "jobs", icon: "💼", label: "Vagas" },
    { id: "plan", icon: "💳", label: "Plano" },
  ];

  const iniciais = (empresa?.nome || "EM").split(" ").map(p => p[0]).join("").substring(0, 2).toUpperCase();

  return (
    <div style={{ width: 220, background: C.surface, borderRight: `0.5px solid ${C.border}`, display: "flex", flexDirection: "column", minHeight: "calc(100vh - 60px)", flexShrink: 0 }}>
      <div style={{ padding: "20px 16px", flex: 1 }}>
        {items.map(item => (
          <div key={item.id} onClick={() => setActive(item.id)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 10, cursor: "pointer", background: active === item.id ? C.rosePale : "transparent", color: active === item.id ? C.roseMid : C.textSec, fontSize: 13, fontWeight: active === item.id ? 500 : 400, marginBottom: 2, transition: "all 0.15s" }}>
            <span style={{ fontSize: 16 }}>{item.icon}</span>{item.label}
          </div>
        ))}
      </div>
      <div style={{ padding: "16px", borderTop: `0.5px solid ${C.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <Avatar initials={iniciais} size={34} radius={10} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {empresa?.nome || "Empresa"}
            </div>
            {empresa?.status === "aprovado" && <Badge label="Verificada" variant="live" />}
            {empresa?.status === "pendente" && <Badge label="Em análise" variant="draft" />}
          </div>
        </div>
        <div onClick={() => { logout(); navigate("/"); }} style={{ fontSize: 12, color: C.textSec, cursor: "pointer" }}>← Sair</div>
      </div>
    </div>
  );
}

// ── Visão geral ────────────────────────────────────────────────────────────────
function Overview({ setActive, empresa, avaliacoes }) {
  const total = avaliacoes.length;
  const notaGeral = media(avaliacoes, "nota_geral");
  const recentes = avaliacoes.slice(0, 2);

  return (
    <div style={{ padding: "28px 32px" }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 700 }}>{empresa?.nome}</h1>
        <p style={{ fontSize: 14, color: C.textSec, marginTop: 4 }}>Resumo da sua reputação na WoMom</p>
      </div>

      {empresa?.status === "pendente" && (
        <div style={{ background: "#FEF3C7", border: "0.5px solid #F59E0B", borderRadius: 12, padding: "14px 18px", marginBottom: 24, fontSize: 13, color: "#92400E", lineHeight: 1.6 }}>
          Seu cadastro está em análise. Assim que for aprovado, o perfil da empresa fica visível nas buscas do site.
        </div>
      )}

      {empresa?.status === "rejeitado" && (
        <div style={{ background: C.redBg, border: "0.5px solid #F7C1C1", borderRadius: 12, padding: "14px 18px", marginBottom: 24, fontSize: 13, color: C.red, lineHeight: 1.6 }}>
          Seu cadastro não foi aprovado{empresa?.motivo_rejeicao ? `: ${empresa.motivo_rejeicao}` : "."} Entre em contato para mais informações.
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 14, marginBottom: 24, maxWidth: 520 }}>
        <MetricCard value={notaGeral ? notaGeral.toFixed(1) : "—"} label="Nota média geral" />
        <MetricCard value={String(total)} label={total === 1 ? "Avaliação publicada" : "Avaliações publicadas"} />
      </div>

      {total === 0 ? (
        <Vazio
          titulo="Nenhuma avaliação ainda"
          texto="Quando alguma mulher que trabalhou na sua empresa publicar uma avaliação, ela aparece aqui com as notas por critério."
        />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <Card>
            <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 16 }}>Critérios específicos</div>
            {CRITERIOS.map(({ campo, label }) => {
              const valor = media(avaliacoes, campo);
              if (valor === null) return null;
              const pct = (valor / 5) * 100;
              return (
                <div key={campo} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <div style={{ fontSize: 12, color: C.textSec, width: 160, flexShrink: 0 }}>{label}</div>
                  <div style={{ flex: 1, height: 5, background: C.grayBg, borderRadius: 100, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${pct}%`, background: valor < 3 ? "#E24B4A" : C.roseMid, borderRadius: 100 }} />
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 500, width: 28, textAlign: "right" }}>{valor.toFixed(1)}</div>
                </div>
              );
            })}
          </Card>

          <Card>
            <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 16, display: "flex", justifyContent: "space-between" }}>
              Avaliações recentes
              <span onClick={() => setActive("reviews")} style={{ fontSize: 12, color: C.roseMid, cursor: "pointer", fontWeight: 400 }}>ver todas</span>
            </div>
            {recentes.map((r, i) => (
              <div key={r.id} style={{ borderBottom: i === 0 && recentes.length > 1 ? `0.5px solid ${C.border}` : "none", paddingBottom: i === 0 && recentes.length > 1 ? 14 : 0, marginBottom: i === 0 && recentes.length > 1 ? 14 : 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{r.cargo || "Cargo não informado"}</span>
                  <span style={{ fontSize: 11, color: C.textSec, marginLeft: "auto" }}>
                    {new Date(r.created_at).toLocaleDateString("pt-BR")}
                  </span>
                </div>
                <Stars value={r.nota_geral} size={12} />
                {r.comentario && <p style={{ fontSize: 12, color: C.textSec, marginTop: 4, lineHeight: 1.5 }}>"{r.comentario}"</p>}
              </div>
            ))}
          </Card>
        </div>
      )}
    </div>
  );
}

// ── Avaliações ─────────────────────────────────────────────────────────────────
function Reviews({ avaliacoes }) {
  const [filtroNota, setFiltroNota] = useState("todas");

  const lista = avaliacoes.filter(r => {
    if (filtroNota === "todas") return true;
    if (filtroNota === "altas") return r.nota_geral >= 4;
    if (filtroNota === "baixas") return r.nota_geral <= 3;
    return true;
  });

  return (
    <div style={{ padding: "28px 32px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700 }}>Avaliações</h1>
          <p style={{ fontSize: 14, color: C.textSec, marginTop: 4 }}>
            {avaliacoes.length === 0 ? "Nenhuma avaliação publicada" : `${avaliacoes.length} ${avaliacoes.length === 1 ? "avaliação publicada" : "avaliações publicadas"}`}
          </p>
        </div>
        {avaliacoes.length > 0 && (
          <select value={filtroNota} onChange={e => setFiltroNota(e.target.value)} style={{ border: `0.5px solid ${C.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 13, background: "#fff" }}>
            <option value="todas">Todas as notas</option>
            <option value="altas">4 e 5 estrelas</option>
            <option value="baixas">1 a 3 estrelas</option>
          </select>
        )}
      </div>

      {avaliacoes.length === 0 ? (
        <Vazio
          titulo="Nenhuma avaliação ainda"
          texto="As avaliações passam por moderação antes de serem publicadas. Quando a primeira for aprovada, ela aparece aqui."
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {lista.map(r => (
            <Card key={r.id} style={{ padding: "20px 24px" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{r.cargo || "Cargo não informado"}</div>
                  {r.periodo && <div style={{ fontSize: 12, color: C.textSec }}>{r.periodo}</div>}
                </div>
                <div style={{ marginLeft: "auto", textAlign: "right" }}>
                  <Stars value={r.nota_geral} />
                  <div style={{ fontSize: 11, color: C.textSec, marginTop: 2 }}>
                    {new Date(r.created_at).toLocaleDateString("pt-BR")}
                  </div>
                </div>
              </div>

              {r.comentario && <p style={{ fontSize: 14, color: C.text, lineHeight: 1.65, marginBottom: 10 }}>"{r.comentario}"</p>}

              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {(r.pontos_positivos || []).map(p => <Chip key={`p-${p}`} label={p} variant="good" />)}
                {(r.pontos_negativos || []).map(p => <Chip key={`n-${p}`} label={p} variant="bad" />)}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Vagas ──────────────────────────────────────────────────────────────────────
function Jobs({ empresa, vagas, recarregar }) {
  const [mostrarForm, setMostrarForm] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [form, setForm] = useState({ titulo: "", area: "", modalidade: "", cidade: "", estado: "" });
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const podePublicar = empresa?.status === "aprovado";

  const publicar = async () => {
    if (!form.titulo || !form.modalidade) {
      setErro("Informe pelo menos o cargo e a modalidade.");
      return;
    }
    setSalvando(true);
    setErro("");

    const { error } = await supabase.from("vagas").insert({
      empresa_id: empresa.id,
      titulo: form.titulo.trim(),
      area: form.area || null,
      modalidade: form.modalidade,
      cidade: form.cidade || empresa.cidade,
      estado: form.estado || empresa.estado,
      status: "pendente",
    });

    setSalvando(false);

    if (error) {
      setErro("Não foi possível publicar a vaga. Tente novamente.");
      return;
    }

    setForm({ titulo: "", area: "", modalidade: "", cidade: "", estado: "" });
    setMostrarForm(false);
    recarregar();
  };

  const rotuloStatus = { aprovado: "Ativa", pendente: "Em análise", rejeitado: "Rejeitada" };

  return (
    <div style={{ padding: "28px 32px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700 }}>Vagas</h1>
          <p style={{ fontSize: 14, color: C.textSec, marginTop: 4 }}>
            {vagas.length === 0 ? "Nenhuma vaga cadastrada" : `${vagas.length} ${vagas.length === 1 ? "vaga" : "vagas"}`}
          </p>
        </div>
        {podePublicar && !mostrarForm && <Btn onClick={() => setMostrarForm(true)}>+ Nova vaga</Btn>}
      </div>

      {!podePublicar && (
        <div style={{ background: "#FEF3C7", border: "0.5px solid #F59E0B", borderRadius: 12, padding: "14px 18px", marginBottom: 24, fontSize: 13, color: "#92400E", lineHeight: 1.6 }}>
          Você poderá publicar vagas assim que o cadastro da empresa for aprovado.
        </div>
      )}

      {mostrarForm && (
        <Card style={{ marginBottom: 24, padding: "24px" }}>
          <h3 style={{ fontSize: 16, fontWeight: 500, marginBottom: 16 }}>Nova vaga</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ fontSize: 13, fontWeight: 500, color: C.textSec }}>Cargo *</label>
              <input value={form.titulo} onChange={set("titulo")} placeholder="Ex: Engenheira de Software" style={{ border: `0.5px solid ${C.border}`, borderRadius: 8, padding: "9px 12px", fontSize: 13 }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ fontSize: 13, fontWeight: 500, color: C.textSec }}>Área</label>
              <input value={form.area} onChange={set("area")} placeholder="Ex: Tecnologia" style={{ border: `0.5px solid ${C.border}`, borderRadius: 8, padding: "9px 12px", fontSize: 13 }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ fontSize: 13, fontWeight: 500, color: C.textSec }}>Modalidade *</label>
              <select value={form.modalidade} onChange={set("modalidade")} style={{ border: `0.5px solid ${C.border}`, borderRadius: 8, padding: "9px 12px", fontSize: 13, background: "#fff" }}>
                <option value="">Selecione...</option>
                <option>Remoto</option><option>Híbrido</option><option>Presencial</option>
              </select>
            </div>
          </div>

          <div style={{ fontSize: 12, color: C.textSec, marginBottom: 16, lineHeight: 1.6 }}>
            A vaga passa por moderação antes de ficar visível no site.
          </div>

          {erro && <div style={{ fontSize: 13, color: C.red, marginBottom: 14 }}>{erro}</div>}

          <div style={{ display: "flex", gap: 10 }}>
            <Btn variant="secondary" onClick={() => { setMostrarForm(false); setErro(""); }}>Cancelar</Btn>
            <Btn onClick={publicar} disabled={salvando}>{salvando ? "Enviando..." : "Enviar vaga"}</Btn>
          </div>
        </Card>
      )}

      {vagas.length === 0 && !mostrarForm ? (
        <Vazio
          titulo="Nenhuma vaga cadastrada"
          texto="Publique vagas para alcançar mulheres que buscam empresas comprometidas com diversidade."
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {vagas.map(v => (
            <Card key={v.id} style={{ padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>{v.titulo}</div>
                <div style={{ fontSize: 12, color: C.textSec }}>
                  {[v.area, v.modalidade, v.cidade && `${v.cidade}${v.estado ? `/${v.estado}` : ""}`].filter(Boolean).join(" · ")}
                </div>
              </div>
              <Badge
                label={rotuloStatus[v.status] || v.status}
                variant={v.status === "aprovado" ? "live" : "draft"}
              />
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Plano ──────────────────────────────────────────────────────────────────────
function PlanTab() {
  const navigate = useNavigate();

  return (
    <div style={{ padding: "28px 32px" }}>
      <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, marginBottom: 24 }}>Plano</h1>

      <Card style={{ maxWidth: 560 }}>
        <div style={{ fontSize: 12, color: C.textSec, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 6 }}>Plano atual</div>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, marginBottom: 12 }}>Gratuito</div>
        <p style={{ fontSize: 14, color: C.textSec, lineHeight: 1.7, marginBottom: 20 }}>
          Seu perfil está ativo sem custo. Você pode acompanhar as avaliações que recebe e publicar vagas.
        </p>
        <Btn variant="secondary" onClick={() => navigate("/planos")}>Ver planos para empresas</Btn>
      </Card>
    </div>
  );
}

// ── Dashboard ──────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const [active, setActive] = useState("overview");
  const [empresa, setEmpresa] = useState(null);
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [vagas, setVagas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const { user, loading: authLoading } = useApp();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !user) navigate("/login");
  }, [user, authLoading, navigate]);

  const carregar = async () => {
    if (!user) return;

    const { data: dadosEmpresa } = await supabase
      .from("empresas")
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();

    if (!dadosEmpresa) {
      setCarregando(false);
      return;
    }
    setEmpresa(dadosEmpresa);

       const { data: dadosAvaliacoes, error: erroAvaliacoes } = await supabase
      .from("avaliacoes_da_minha_empresa")
      .select("*")
      .eq("empresa_id", dadosEmpresa.id)
      .order("created_at", { ascending: false })
      .order("id", { ascending: false });
    if (erroAvaliacoes) console.error("Erro ao carregar avaliacoes:", erroAvaliacoes);
    setAvaliacoes(dadosAvaliacoes || []);

    const { data: dadosVagas } = await supabase
      .from("vagas")
      .select("*")
      .eq("empresa_id", dadosEmpresa.id)
      .order("created_at", { ascending: false });
    setVagas(dadosVagas || []);

    setCarregando(false);
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line
  }, [user]);

  if (authLoading || carregando) {
    return (
      <div style={{ minHeight: "calc(100vh - 60px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <p style={{ color: C.textSec }}>Carregando...</p>
      </div>
    );
  }

  if (!user) return null;

  if (!empresa) {
    return (
      <div style={{ minHeight: "calc(100vh - 60px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <Card style={{ maxWidth: 460, padding: "40px 32px", textAlign: "center" }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, marginBottom: 10 }}>
            Esta conta não é de empresa
          </h2>
          <p style={{ fontSize: 14, color: C.textSec, lineHeight: 1.7, marginBottom: 24 }}>
            O painel é exclusivo para empresas cadastradas. Se você quer cadastrar sua empresa, comece por aqui.
          </p>
          <Btn full onClick={() => navigate("/cadastro-empresa")}>Cadastrar empresa</Btn>
          <Btn variant="ghost" full onClick={() => navigate("/")} style={{ marginTop: 8, fontSize: 13 }}>Voltar para o início</Btn>
        </Card>
      </div>
    );
  }

  const tabs = {
    overview: <Overview setActive={setActive} empresa={empresa} avaliacoes={avaliacoes} />,
    reviews: <Reviews avaliacoes={avaliacoes} />,
    jobs: <Jobs empresa={empresa} vagas={vagas} recarregar={carregar} />,
    plan: <PlanTab />,
  };

  return (
    <div style={{ display: "flex" }}>
      <Sidebar active={active} setActive={setActive} empresa={empresa} />
      <div style={{ flex: 1, minHeight: "calc(100vh - 60px)", overflow: "auto" }}>
        {tabs[active] || tabs.overview}
      </div>
    </div>
  );
}
